import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * usePagination - a generic hook for implementing backend-driven pagination and search.
 * @param {Function} fetchFunction    - required; async (params) => API response (expects paginated shape)
 * @param {number}   initialPageSize  - initial pageSize (default: 30)
 * @param {string}   initialSearch    - initial search string (default: '')
 * @param {boolean}  enabled          - whether to auto-fetch on mount/change (default: true)
 * @param {boolean}  manualSearch     - if true, search is triggered manually instead of on change (default: false)
 */
export const usePagination = (
  fetchFunction,
  initialPageSize = 30,
  initialSearch = '',
  enabled = true,
  manualSearch = false
) => {
  const [data, setData] = useState(null);   // Full API response
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  
  // For manual search mode, separate input state from active search
  const [searchInput, setSearchInput] = useState(manualSearch ? initialSearch : '');
  const [activeSearch, setActiveSearch] = useState(manualSearch ? '' : initialSearch);

  // Track last fetch parameters to prevent duplicate requests
  const lastFetchParamsRef = useRef({ page: null, pageSize: null, search: null });
  // Track the fetchFunction we last fetched with. When a caller's filter
  // changes, it passes a NEW (useCallback-memoized) fetchFunction even though
  // page/pageSize/search are unchanged — without this, the param-key guard
  // below would skip the refetch and the list would not reflect the new
  // filter (e.g. the Buyers "Shortlisted / All / Without" tabs).
  const lastFetchFnRef = useRef(null);
  const isLoadingRef = useRef(false);

  // Active search term for the current mode. Extracted to a single derived
  // value so the hook deps can reference it directly (avoids the
  // "complex expression in dependency array" lint and keeps deps complete).
  const searchValue = manualSearch ? activeSearch : searchInput;

  // main fetch logic
  const fetchData = useCallback(async () => {
    // Prevent duplicate calls if already loading
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const res = await fetchFunction({ page, page_size: pageSize, search: searchValue });
      lastFetchParamsRef.current = { page, pageSize, search: searchValue };
      setData(res);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [fetchFunction, page, pageSize, searchValue]);

  useEffect(() => {
    if (!enabled) return;

    const fetchKey = `${page}-${pageSize}-${searchValue}`;
    const lastKey = `${lastFetchParamsRef.current.page}-${lastFetchParamsRef.current.pageSize}-${lastFetchParamsRef.current.search}`;
    // A changed fetchFunction means the caller's filter/scope changed (callers
    // memoize it via useCallback), so refetch even when page/size/search match.
    const fnChanged = lastFetchFnRef.current !== fetchFunction;

    // Only fetch if parameters or the fetch function changed, and not already loading
    if ((fetchKey !== lastKey || fnChanged) && !isLoadingRef.current) {
      lastFetchFnRef.current = fetchFunction;
      fetchData();
    }
  }, [enabled, page, pageSize, searchValue, fetchFunction, fetchData]);

  const goToPage = (newPage) => {
    setPage(newPage);
  };

  const changePageSize = (newSize) => {
    // Batch state updates to prevent double fetch
    // Use functional updates to ensure both happen in the same render cycle
    setPageSize(newSize);
    setPage(1); // when changing page size, reset to first page
    // Both state updates are batched in React 18+, so fetchData will only be recreated once
  };

  // For automatic search mode
  const changeSearch = (val) => {
    if (!manualSearch) {
      setSearchInput(val);
      setPage(1); // when changing search, reset to first page
    }
  };

  // For manual search mode
  const handleSearchInputChange = (val) => {
    if (manualSearch) {
      setSearchInput(val);
    }
  };

  const triggerSearch = () => {
    if (manualSearch) {
      setActiveSearch(searchInput);
      setPage(1); // when triggering search, reset to first page
    }
  };

  // Return appropriate props based on manualSearch mode
  if (manualSearch) {
    return {
      data: data?.items || [],
      pagination: {
        page,
        pageSize,
        total: data?.total || 0,
        totalPages: data?.total_pages || 0,
        hasNext: data?.has_next || false,
        hasPrevious: data?.has_previous || false,
      },
      searchInput,
      activeSearch,
      loading,
      error,
      goToPage,
      changePageSize,
      handleSearchInputChange,
      triggerSearch,
      refetch: fetchData,
    };
  }

  return {
    data: data?.items || [],
    pagination: {
      page,
      pageSize,
      total: data?.total || 0,
      totalPages: data?.total_pages || 0,
      hasNext: data?.has_next || false,
      hasPrevious: data?.has_previous || false,
    },
    search: searchInput,
    loading,
    error,
    goToPage,
    changePageSize,
    changeSearch,
    refetch: fetchData,
  };
};

export default usePagination;

